
declare global {
  interface Window {
    dataLayer?: any[];
    gtag?: (...args: any[]) => void;
  }
}

const DEFAULT_GA_MEASUREMENT_ID = 'G-68PE5HZ6WL';
const CONSENT_STORAGE_KEY = 'upfixe-cookie-consent';

const getMeasurementId = (measurementId?: string) =>
  measurementId || import.meta.env.VITE_GA_MEASUREMENT_ID || DEFAULT_GA_MEASUREMENT_ID;

const ensureDataLayer = () => {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
};

const ensureGtag = (measurementId: string) => {
  if (typeof window === 'undefined') return;

  ensureDataLayer();

  if (!window.gtag) {
    window.gtag = function(...args: any[]) {
      window.dataLayer?.push(arguments);
    };
  }
};

const hasAnalyticsConsent = () => {
  if (typeof window === 'undefined') return false;

  try {
    const storedConsent = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!storedConsent) return false;

    const parsedConsent = JSON.parse(storedConsent) as { analytics?: boolean };
    return parsedConsent.analytics === true;
  } catch (error) {
    console.error('Failed to read analytics consent:', error);
    return false;
  }
};

/**
 * Track an event in Google Analytics
 * @param eventName The name of the event to track
 * @param eventProperties Additional properties to send with the event
 */
export const trackEvent = (
  eventName: string,
  eventProperties?: string | Record<string, any>
) => {
  if (typeof window === 'undefined') return;
  
  try {
    // Convert string eventProperties to object if needed
    const properties = typeof eventProperties === 'string' 
      ? { action: eventProperties } 
      : eventProperties || {};

    if (!window.gtag) {
      ensureGtag(getMeasurementId());
    }

    window.gtag?.('event', eventName, properties);
    
    console.log(`Tracked event: ${eventName}`, properties);
  } catch (error) {
    console.error(`Failed to track event: ${eventName}`, error);
  }
};

/**
 * Initialize Google Analytics directly
 * @param measurementId GA4 measurement ID
 */
export const initializeGoogleAnalytics = async (measurementId?: string) => {
  if (typeof window === 'undefined') return;

  const resolvedMeasurementId = getMeasurementId(measurementId);

  try {
    ensureGtag(resolvedMeasurementId);

    if (hasAnalyticsConsent()) {
      await updateAnalyticsConsent(true, resolvedMeasurementId);
    }

    console.log(`Google Analytics initialized with measurement ID: ${resolvedMeasurementId}`);
  } catch (error) {
    console.error('Failed to initialize Google Analytics:', error);
  }
};

/**
 * Update Google Analytics consent and load the script once analytics consent is granted.
 */
export const updateAnalyticsConsent = async (
  analyticsGranted: boolean,
  measurementId?: string
) => {
  if (typeof window === 'undefined') return;

  const resolvedMeasurementId = getMeasurementId(measurementId);

  try {
    if (!analyticsGranted) {
      ensureGtag(resolvedMeasurementId);
      window.gtag?.('consent', 'update', {
        analytics_storage: 'denied',
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
      });
      return;
    }

    ensureGtag(resolvedMeasurementId);
    window.gtag?.('consent', 'update', {
      analytics_storage: 'granted',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    });
  } catch (error) {
    console.error('Failed to update Google Analytics consent:', error);
  }
};

/**
 * Track a page view in Google Analytics
 */
export const trackPageView = () => {
  if (typeof window === 'undefined') return;

  try {
    const path = window.location.pathname;
    console.log("Tracked page view:", path);
    
    if (!window.gtag) {
      ensureGtag(getMeasurementId());
    }

    window.gtag?.('event', 'page_view', {
      page_path: path,
      page_location: window.location.href,
      page_title: document.title
    });
  } catch (error) {
    console.error("Failed to track page view:", error);
  }
};

/**
 * Track a conversion in Google Analytics
 * @param conversionId ID of the conversion
 * @param value Value of the conversion (optional)
 * @param currency Currency of the value (optional)
 */
export const trackConversion = (conversionId: string, value?: number, currency: string = 'USD') => {
  try {
    const eventData: Record<string, any> = {
      conversion_id: conversionId
    };
    
    if (value !== undefined) {
      eventData.value = value;
      eventData.currency = currency;
    }
    
    trackEvent('conversion', eventData);
  } catch (error) {
    console.error(`Failed to track conversion ${conversionId}:`, error);
  }
};

/**
 * Track a user registration
 */
export const trackSignUp = () => {
  trackEvent('sign_up');
};

/**
 * Track a successful login
 */
export const trackLogin = () => {
  trackEvent('login');
};

/**
 * Track an ecommerce transaction
 */
export const trackPurchase = (
  transactionId: string,
  value: number,
  currency: string = 'USD',
  items?: Array<{
    item_id: string;
    item_name: string;
    price: number;
    quantity: number;
  }>
) => {
  trackEvent('purchase', {
    transaction_id: transactionId,
    value,
    currency,
    items
  });
};
